// The site map — the real map engine, with the MUR 1 km field over it.
//
// THIS WAS A HAND-ROLLED CANVAS AND IT SHOULD NOT HAVE BEEN. The first version
// drew the raster into a bare <canvas> and traced its own coastline out of the
// land mask. It rendered the field correctly and still looked crude, and the
// reason was not the field: there was NO BASEMAP. No coast, no bathymetry, no
// place names — a colour swatch with two dots on it.
//
// `map/map.ts` already carries an Esri Ocean raster basemap (CORS-friendly, no
// key) behind `createMap()`, which is most of what makes the fleet console's map
// read as a map. Using it here costs one import and deletes an entire hand-drawn
// coastline.
//
// WHAT IS AND IS NOT REUSED, since this repo's rule is not to write a second
// renderer (report/sitemap.ts: "the field, from map/windfield.ts's own renderer,
// NOT a second copy of it"):
//
//   REUSED   map/map.ts — the engine, the Esri basemap, the site marker, the
//            theming, the attribution.
//   NOT      windfield.ts's renderFieldHour(). It takes a FieldMesh — wind and
//            wave planes with times and a NaN sea mask — so ours would have to
//            arrive disguised as one. Its ramp is the WORKABILITY ramp, green
//            through amber to red, which must not appear on a field that carries
//            no good/bad meaning at all (below). What is borrowed is the
//            technique, and this comment is the record of that choice.
//
// ⚠️ THE FIELD IS NOT GRADED AGAINST THE LICENCE THRESHOLD, and an earlier
// version of this file was. Anchoring the ramp to the armed threshold painted
// 330 of 415 wet cells at or over 64 °F while the verdict beside it read NORMAL
// with 4.5 °F of margin — a screen contradicting itself. MUR is a SATELLITE SKIN
// temperature (63.8–65.0 °F here, against a buoy daily minimum of 57.3): it
// measures the sun-warmed surface layer that makes the buoy's raw signal
// useless, which is why every buoy figure in this product says *daily minimum*.
// Grading it against an intake bulk-water limit conflates two quantities — the
// exact mistake the daily-minimum work exists to prevent. So the ramp is
// sequential over the field's own range, and the caption says which layer it is.
//
// MERCATOR. A MapLibre image source is stretched linearly across its corner quad
// in Mercator space, so a raster spaced linearly in LATITUDE lands progressively
// wrong toward the poles — windfield.ts records this biting it at basin scale.
// Here the box spans 0.22° of latitude at 35 °N, where linear-in-latitude and
// linear-in-Mercator differ by under a tenth of a percent of the frame: well
// under a pixel. Stated rather than assumed, because the same shortcut on a
// wider box would be a real misregistration.

import maplibregl from "maplibre-gl";
import { createMap, type SeaMap } from "../../map/map.js";
import type { SstField, SourcePoint } from "./payload.js";

export interface SiteMapConfig {
  field: SstField | null;
  /** Every measuring point the forecast depends on, marked and clickable. */
  sources?: SourcePoint[];
  /** Called when one is clicked, so the console can show it against the intake. */
  onPickSource?: (s: SourcePoint) => void;
  /** The bulk-water reading this field must NOT be confused with — the buoy's
   *  daily minimum, °F. Printed alongside so the gap is stated rather than left
   *  for a reader to misread as headroom. */
  bulkRef?: number | null;
  site?: { lat?: number; lon?: number };
  buoy?: { lat: number; lon: number };
}

export interface SiteMap {
  update(cfg: Partial<SiteMapConfig>): void;
  /** Show or hide one layer. Additive and persistent, per rail.ts's rule that
   *  layers are not workspaces. */
  setLayer(id: string, on: boolean): void;
  destroy(): void;
  /** For tests: wet cells in the last raster built. */
  paintedCells(): number;
  /**
   * For tests: can the viewer actually REACH every source?
   *
   * maxBounds silently caps zoom-out — the map does not error, it just refuses
   * to go further — so "NDBC 46218 is unreachable at any zoom" looked identical
   * to a map working correctly. `paintedCells` answers what was drawn; this
   * answers what can be got to, which is the question that was wrong.
   */
  reach(): {
    zoom: number;
    minZoom: number;
    /** true when the pen is at least as wide on the ground as it is tall, which
     *  is what lets any viewport aspect frame the whole network. */
    boundsFramable: boolean;
    /** source name -> inside the current viewport */
    inView: Record<string, boolean>;
  };
}

/**
 * Grow a lon/lat box east-west until it is at least as wide on the ground as it
 * is tall. Returns a new pair; the input is not mutated.
 *
 * WHY A SQUARE-ISH PEN. maxBounds stops the map displaying ground outside it,
 * which also caps zoom-out — and the cap is set by whichever dimension runs out
 * first. Diablo Canyon's measuring network is 84 km north-south and about 10 km
 * east-west, so the natural pen came out 24 km wide by 102 km tall. On a roughly
 * square map pane the zoom-out clamped against the 24 km WIDTH, and the 102 km
 * height could never be framed: NDBC 46218 was unreachable at every zoom.
 *
 * Nothing reported this. The map did not error or warn; it simply stopped
 * zooming, which is indistinguishable from a map that has finished zooming. It
 * survived a round of "make all four sources visible" because opening with
 * fitBounds on the network merely opened AT the clamp — showing open water, no
 * facility, and still no 46218.
 *
 * Only ever grows, so a network that is already wide keeps its own pen.
 */
export function widenToFramable(
  sw: [number, number],
  ne: [number, number],
): [[number, number], [number, number]] {
  const midLat = (sw[1] + ne[1]) / 2;
  const kmPerDegLon = 111.32 * Math.cos((midLat * Math.PI) / 180);
  const degLon = ne[0] - sw[0];
  const kmTall = (ne[1] - sw[1]) * 110.57;
  if (kmPerDegLon <= 0 || degLon * kmPerDegLon >= kmTall) {
    return [[sw[0], sw[1]], [ne[0], ne[1]]];
  }
  const grow = (kmTall / kmPerDegLon - degLon) / 2;
  return [[sw[0] - grow, sw[1]], [ne[0] + grow, ne[1]]];
}

const C2F = (c: number): number => (c * 9) / 5 + 32;
const SRC = "thermal-sst";
const PTS = "thermal-sources";
const IMG = "thermal-imagery";

// SATELLITE IMAGERY, not the ocean basemap, and the reason is recognition.
//
// map/map.ts ships Esri's Ocean basemap capped at z10 and overzoomed past it —
// right for a fleet working ocean basins, and at this site it is an upscaled
// z10 tile: a soft blue-grey wash with no shoreline anyone could place. This
// client has ONE installation, and the first thing they should be able to do is
// recognise it: the breakwaters, the intake cove inside them, the marina and
// boat ramp south of the reactors, the discharge at 001.
//
// Esri World Imagery serves real tiles to z17+ here (probed 2026-08 at the site:
// 200 image/jpeg, Access-Control-Allow-Origin *), needs no key, and comes from
// the same arcgisonline host the console already trusts for the ocean tiles and
// the BOEM/WDPA overlays — so this adds a layer, not a dependency.
//
// The Ocean base is HIDDEN rather than removed: it belongs to the shared style
// that map/map.ts builds, and a console that deleted a layer out of the shared
// style would be reaching into the fleet console's furniture to do it.
const IMAGERY_TILES =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

/** Sequential, one hue, light→dark: pale sand to deep rust. ONE hue family and
 *  deliberately NOT the workability ramp — this field carries magnitude, never
 *  good or bad. It used to be a blue ramp, which over blue water at 75 %
 *  opacity was invisible at every zoom the console actually opens at (Cody,
 *  2026-10-06: "I've never even seen it on the map?"). A warm hue is the one
 *  thing the ocean is not. Light = cooler, dark = warmer; the caption says so. */
const STOPS: [number, number, number][] = [
  [255, 243, 214], [252, 220, 160], [246, 190, 112],
  [235, 152, 72], [214, 108, 44], [178, 66, 26], [122, 36, 12],
];

function rampRGB(x: number): [number, number, number] {
  const t = Number.isFinite(x) ? Math.max(0, Math.min(1, x)) : 0;
  const f = t * (STOPS.length - 1);
  const i = Math.min(STOPS.length - 2, Math.floor(f));
  const w = f - i;
  const a = STOPS[i] as [number, number, number];
  const b = STOPS[i + 1] as [number, number, number];
  return [
    Math.round(a[0] + (b[0] - a[0]) * w),
    Math.round(a[1] + (b[1] - a[1]) * w),
    Math.round(a[2] + (b[2] - a[2]) * w),
  ];
}

/** Nearest wet value to a land cell, so the smoothing the GPU applies has ocean
 *  on both sides of the shoreline instead of dragging transparency inland. */
function nearestWet(
  grid: (number | null)[], cols: number, rows: number, r0: number, c0: number,
): number | null {
  for (let rad = 1; rad < Math.max(cols, rows); rad++) {
    for (let dr = -rad; dr <= rad; dr++) {
      for (let dc = -rad; dc <= rad; dc++) {
        if (Math.max(Math.abs(dr), Math.abs(dc)) !== rad) continue;
        const r = r0 + dr;
        const c = c0 + dc;
        if (r < 0 || c < 0 || r >= rows || c >= cols) continue;
        const v = grid[r * cols + c];
        if (v !== null && v !== undefined) return v;
      }
    }
  }
  return null;
}

interface Raster {
  url: string;
  coords: [[number, number], [number, number], [number, number], [number, number]];
  loF: number;
  hiF: number;
  wet: number;
}

/** The field as a PNG data URL plus its corner quad. Land is TRANSPARENT so the
 *  Esri coastline shows through — drawing our own over it would be a second,
 *  coarser opinion about where the shore is. */
function buildRaster(f: SstField): Raster | null {
  const rows = f.rows ?? 0;
  const cols = f.cols ?? 0;
  const grid = f.grid;
  if (rows <= 0 || cols <= 0 || !Array.isArray(grid)) return null;

  let lo = Infinity;
  let hi = -Infinity;
  let wet = 0;
  for (const v of grid) {
    if (v === null || v === undefined) continue;
    wet++;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (wet === 0) return null;
  const span = hi - lo;

  const cv = document.createElement("canvas");
  cv.width = cols;
  cv.height = rows;
  const ctx = cv.getContext("2d");
  if (ctx === null) return null;
  const img = ctx.createImageData(cols, rows);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const raw = grid[r * cols + c];
      const isWet = raw !== null && raw !== undefined;
      const v = isWet ? (raw as number) : nearestWet(grid, cols, rows, r, c);
      const [rr, gg, bb] = rampRGB(v === null || span <= 0 ? 0.5 : (v - lo) / span);
      // Row 0 is the SOUTHERNMOST latitude; an image's row 0 is its top, so rows
      // are written bottom-up. Backwards, this mirrors the coast and still looks
      // like a plausible map — which is why it is spelled out.
      const px = ((rows - 1 - r) * cols + c) * 4;
      img.data[px] = rr;
      img.data[px + 1] = gg;
      img.data[px + 2] = bb;
      img.data[px + 3] = isWet ? 235 : 0;
    }
  }
  ctx.putImageData(img, 0, 0);

  const lat0 = f.lat0 ?? 0;
  const lon0 = f.lon0 ?? 0;
  const dlat = f.dlat ?? 0;
  const dlon = f.dlon ?? 0;
  // Cell CENTRES are what the payload publishes, so the quad runs half a cell
  // beyond the outermost ones. Placing it on the centres shifts the whole field
  // half a kilometre northeast, which is invisible and wrong.
  const w = lon0 - dlon / 2;
  const e = lon0 + dlon * (cols - 0.5);
  const s = lat0 - dlat / 2;
  const n = lat0 + dlat * (rows - 0.5);
  return {
    url: cv.toDataURL("image/png"),
    coords: [[w, n], [e, n], [e, s], [w, s]],
    loF: C2F(lo),
    hiF: C2F(hi),
    wet,
  };
}

export function createSiteMap(host: HTMLElement, initial: SiteMapConfig): SiteMap {
  let cfg: SiteMapConfig = { ...initial };
  let wet = 0;

  const lat = cfg.site?.lat ?? 35.2077;
  const lon = cfg.site?.lon ?? -120.856;

  // A caption strip: the field has to be named, and a map cannot carry a
  // sentence. Colour on a map is never self-describing.
  const cap = document.createElement("div");
  cap.className = "smapcap";
  host.appendChild(cap);

  const map: SeaMap = createMap(host, {
    center: [lon, lat],
    // A starting value only — fitBounds below reframes to the whole measuring
    // network as soon as the style is ready. Imagery serves to z17, so zooming
    // in to the breakwaters works; the field simply leaves the frame, which is
    // honest, since it is a 24 km product and gains nothing by magnification.
    zoom: 12,
    // Nothing to pick: this client has one installation and the point is fixed.
    // Swallowing the click is deliberate — a marker the operator can move would
    // imply the forecast follows it, and it does not.
    onPick: () => undefined,
  });

  const paint = (): void => {
    const f = cfg.field;
    const raster = f === null || f === undefined ? null : buildRaster(f);
    wet = raster?.wet ?? 0;

    if (raster === null) {
      // A statement, not an empty frame: an unlabelled blank map reads as calm
      // water, which is the opposite of unknown.
      cap.replaceChildren(document.createTextNode("No sea-surface field available"));
      return;
    }
    const ref = cfg.bulkRef;
    // The bulk-water reference is printed as a NUMBER beside the field's range,
    // not as a difference from the field's low end. With the field over the
    // whole pen (2026-10-09) its low end is cold offshore water, which sat
    // below the cove buoy's minimum and printed as "-1.4 °F above".
    const gap =
      typeof ref === "number" && Number.isFinite(ref)
        ? ` · buoy 46215 daily minimum ${ref.toFixed(1)} °F, the bulk-water reference`
        : "";
    // A colour legend, not a sentence about colour: the ramp itself with its
    // two ends in degrees (Cody, 2026-10-07: "no legend so the color is
    // meaningless"). The same STOPS the raster is painted from.
    const lo = document.createElement("span");
    lo.className = "smaplegend-v";
    lo.textContent = `${raster.loF.toFixed(1)} °F`;
    const bar = document.createElement("span");
    bar.className = "smaplegend";
    bar.style.background = `linear-gradient(to right, ${STOPS.map((c) => `rgb(${c[0]},${c[1]},${c[2]})`).join(", ")})`;
    bar.title = "Sea-surface temperature, light = cooler, dark = warmer";
    const hi = document.createElement("span");
    hi.className = "smaplegend-v";
    hi.textContent = `${raster.hiF.toFixed(1)} °F`;
    const text = document.createElement("span");
    text.textContent =
      ` · satellite skin temperature, not the intake quantity${gap} · 1 km cells · ` +
      `${f?.source ?? "MUR SST"}${f?.date === undefined ? "" : ` · ${f.date}`}`;
    cap.replaceChildren(lo, bar, hi, text);

    const apply = (): void => {
      const m = map.raw();
      const existing = m.getSource(SRC) as
        | { updateImage?: (o: { url: string; coordinates: unknown }) => void }
        | undefined;
      if (existing !== undefined && typeof existing.updateImage === "function") {
        existing.updateImage({ url: raster.url, coordinates: raster.coords });
        return;
      }
      m.addSource(SRC, { type: "image", url: raster.url, coordinates: raster.coords });
      // Beneath the basemap's label layer, so place names stay readable over the
      // field — those labels are half of what makes this legible as a location.
      const before = m.getLayer("labels") === undefined ? undefined : "labels";
      m.addLayer(
        {
          id: SRC,
          type: "raster",
          source: SRC,
          // Translucent over imagery so the coastline, the breakwaters and the
          // cove stay visible THROUGH the field — opaque, the field wins and we
          // are back to a colour swatch with better provenance.
          //
          // VISIBLE AT EVERY ZOOM THE CONSOLE USES. It used to fade to nothing by
          // z15 on the argument that a 1 km cell magnified over a 50 m view
          // invites reading structure that is not there — true, and the result
          // was that the map opened on the plant at z16 and nobody ever saw the
          // field at all. The caption now states the cell size; the field only
          // lightens as you zoom in, it never leaves.
          paint: {
            "raster-fade-duration": 0,
            "raster-opacity": [
              "interpolate", ["linear"], ["zoom"],
              11, 0.78,
              13, 0.72,
              15, 0.6,
              16.5, 0.5,
            ],
          },
        },
        before,
      );
      // THE IMAGERY MUST SIT UNDER THE FIELD. Both layers are inserted "just
      // beneath the labels", so whichever arrives second lands on top — and the
      // imagery arrives second (its onReady runs after this one) and is opaque.
      // That is why the field was captioned, loaded and never once seen (Cody,
      // 2026-10-06: "I've never even seen it on the map?"). Order is stated,
      // not assumed, in both places the two layers meet.
      if (m.getLayer(IMG) !== undefined) m.moveLayer(IMG, SRC);
    };
    map.onReady(apply);
  };

  map.onReady(() => {
    const m = map.raw();

    // PENNED IN, BUT TO OUR DATA — not to an arbitrary box.
    //
    // This client has one installation and there is nothing to find in the
    // middle of the Pacific, so the map is bounded. But the bound is the SOURCE
    // NETWORK, and that network is bigger than the site: NDBC 46218 sits 84 km
    // south, and it is on this map precisely because a buoy that far offshore
    // tracking the intake is the evidence that the intake measures regional
    // ocean water. A pen drawn around the 24 km field would have hidden the
    // strongest thing the map has to say.
    //
    // Set on the INSTANCE rather than in map/map.ts, so the fleet console stays
    // free-roaming for its nine regions.
    const pts: [number, number][] = [[lon, lat]];
    for (const s of cfg.sources ?? []) {
      if (typeof s.lat === "number" && typeof s.lon === "number") pts.push([s.lon, s.lat]);
    }
    const pad = 0.08;
    const lons = pts.map((q) => q[0]);
    const lats = pts.map((q) => q[1]);
    const sw: [number, number] = [Math.min(...lons) - pad, Math.min(...lats) - pad];
    const ne: [number, number] = [Math.max(...lons) + pad, Math.max(...lats) + pad];
    // THE FIELD'S EDGE IS THE PEN'S EDGE. The field is sized (build_panel.py,
    // MUR_BOX) to cover the whole zoomed-out map — Les, 2026-10-09: the water
    // temperature over all of the zoomed-out area, not a patch at the cove.
    // Padding its corners like points pushed the pen 9 km past the field on
    // every side, so the map's margins were bare sea. The field extends the
    // pen; it is not padded. Edges are the cells' outer edges, as buildRaster
    // places them.
    const f0 = cfg.field;
    if (f0 !== null && f0 !== undefined) {
      const w0 = (f0.lon0 ?? lon) - (f0.dlon ?? 0) / 2;
      const s0 = (f0.lat0 ?? lat) - (f0.dlat ?? 0) / 2;
      const e0 = w0 + (f0.dlon ?? 0) * (f0.cols ?? 0);
      const n0 = s0 + (f0.dlat ?? 0) * (f0.rows ?? 0);
      sw[0] = Math.min(sw[0], w0);
      sw[1] = Math.min(sw[1], s0);
      ne[0] = Math.max(ne[0], e0);
      ne[1] = Math.max(ne[1], n0);
    }

    // WIDEN THE PEN UNTIL IT IS AT LEAST AS WIDE AS IT IS TALL.
    //
    // The network runs 84 km north-south and barely 10 km east-west, so the
    // bounds came out 24 km wide by 102 km tall — an aspect of 0.24. MapLibre
    // will not display ground outside maxBounds, so on a roughly square map pane
    // the zoom-out clamps against the WIDTH and the full height can never be
    // framed: NDBC 46218, the source 84 km south, was unreachable at any zoom.
    //
    // This hid behind the previous fix. Opening with fitBounds on the whole
    // network LOOKED like it framed everything, but it was only opening at the
    // clamp limit — which is why the default view was open water with no
    // facility in it and still no 46218.
    //
    // A pen has to be able to contain the thing it surrounds at whatever aspect
    // the viewport happens to be — see widenToFramable.
    const [sw2, ne2] = widenToFramable(sw, ne);
    m.setMaxBounds([sw2, ne2]);
    // OPEN ON THE WHOLE COVERAGE AREA. Les, by text on 2026-10-08, after
    // seeing the satellite field: "I would make the zoomed out view
    // encompassing the coverage area as the initial view." The field now
    // covers the pen, so the opening shot is the sea-surface temperature of
    // the whole measuring network, with the plant a marker inside it; the cove
    // and the breakwaters are a pinch away.
    //
    // This reverses the 2026-10-06 choice to open on the plant, which was
    // made because at the time the field was a 24 km patch and the network
    // view was open water with no facility in it. The client has now seen
    // both and asked for this one. Framed from the pen, so it holds on any
    // viewport.
    m.fitBounds([sw2, ne2], { padding: 8, duration: 0 });
    // Out far enough to see the whole network, in far enough to read the
    // breakwaters — and no further in than the imagery actually serves, or the
    // last step is a blurred upscale pretending to be detail.
    // Low enough that fitBounds can frame an 84 km network on a short viewport.
    m.setMinZoom(7);
    m.setMaxZoom(16.5);

    if (m.getSource(IMG) === undefined) {
      m.addSource(IMG, {
        type: "raster",
        tileSize: 256,
        maxzoom: 17,
        tiles: [IMAGERY_TILES],
        attribution: "Esri World Imagery",
      });
      // Beneath the field and the labels, above nothing — it is the ground.
      // If the field is already on the map, go under IT, not merely under the
      // labels: "under the labels" alone put the imagery over the field.
      const under =
        m.getLayer(SRC) !== undefined ? SRC : m.getLayer("labels") === undefined ? undefined : "labels";
      m.addLayer({ id: IMG, type: "raster", source: IMG,
                   paint: { "raster-fade-duration": 0 } }, under);
    }
    // Hide, do not remove: the ocean layer belongs to the shared style.
    if (m.getLayer("ocean") !== undefined) {
      m.setLayoutProperty("ocean", "visibility", "none");
    }
    map.setPoint(lat, lon);
    drawSources(m);
    paint();
  });

  /**
   * The measuring network: every point the forecast depends on, marked and
   * clickable. Follows the fleet console's buoy overlay — geojson source,
   * circle layer, hover popup, click handler (map/overlays.ts) — because an
   * operator who has used that console should not have to learn a second idiom
   * for the same gesture.
   *
   * The intake is drawn DIFFERENTLY from the rest, and that is the point: it is
   * the only measurement of the quantity being forecast. Everything else is a
   * proxy for it, however good.
   */
  function drawSources(m: ReturnType<SeaMap["raw"]>): void {
    const list = (cfg.sources ?? []).filter(
      (s) => typeof s.lat === "number" && typeof s.lon === "number",
    );
    if (list.length === 0) return;
    const data: GeoJSON.FeatureCollection<GeoJSON.Point> = {
      type: "FeatureCollection",
      features: list.map((s) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [s.lon as number, s.lat as number] },
        properties: {
          id: s.id ?? "",
          name: s.name ?? "",
          kind: s.kind ?? "buoy",
          km: s.km ?? 0,
          r: s.vs_intake?.r ?? null,
        },
      })),
    };
    const existing = m.getSource(PTS) as { setData?: (d: unknown) => void } | undefined;
    if (existing !== undefined && typeof existing.setData === "function") {
      existing.setData(data);
      return;
    }
    m.addSource(PTS, { type: "geojson", data });
    m.addLayer({
      id: PTS,
      type: "circle",
      source: PTS,
      paint: {
        // The intake reads larger — it is the measurement, not a proxy for one.
        "circle-radius": [
          "interpolate", ["linear"], ["zoom"],
          8, ["case", ["==", ["get", "kind"], "intake"], 6, 4],
          14, ["case", ["==", ["get", "kind"], "intake"], 10, 7],
        ],
        "circle-color": [
          "case", ["==", ["get", "kind"], "intake"], "#b8123f", "#f2b134",
        ],
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 1.5,
      },
    });
    m.addLayer({
      id: `${PTS}-label`,
      type: "symbol",
      source: PTS,
      layout: {
        "text-field": ["get", "name"],
        "text-size": 11,
        "text-offset": [0, 1.4],
        "text-anchor": "top",
        "text-allow-overlap": false,
      },
      paint: {
        "text-color": "#16232e",
        // A halo, so a name over dark satellite imagery stays readable without
        // a plaque behind it.
        "text-halo-color": "#ffffff",
        "text-halo-width": 1.6,
      },
    });

    const pop = new maplibregl.Popup({ closeButton: false, closeOnClick: false });
    m.on("mouseenter", PTS, (e) => {
      m.getCanvas().style.cursor = "pointer";
      const f = e.features?.[0];
      if (f === undefined) return;
      const pr = (f.properties ?? {}) as Record<string, unknown>;
      const km = typeof pr["km"] === "number" ? pr["km"] : 0;
      const r = pr["r"];
      const bits = [String(pr["name"] ?? "")];
      bits.push(km < 0.05 ? "the intake itself" : `${km.toFixed(1)} km from the intake`);
      if (typeof r === "number") bits.push(`r = ${r.toFixed(3)} with the intake`);
      bits.push("click to compare");
      const at = (f.geometry as GeoJSON.Point).coordinates as [number, number];
      pop.setLngLat(at).setText(bits.join(" · ")).addTo(m);
    });
    m.on("mouseleave", PTS, () => {
      m.getCanvas().style.cursor = "";
      pop.remove();
    });
    m.on("click", PTS, (e) => {
      const f = e.features?.[0];
      if (f === undefined) return;
      const id = String((f.properties ?? {})["id"] ?? "");
      const hit = list.find((s) => (s.id ?? "") === id);
      if (hit !== undefined) cfg.onPickSource?.(hit);
    });
  }

  return {
    setLayer(id: string, on: boolean): void {
      const m = map.raw();
      const target = id === "field" ? SRC : id === "sources" ? PTS : `${PTS}-label`;
      if (m.getLayer(target) === undefined) return;
      m.setLayoutProperty(target, "visibility", on ? "visible" : "none");
    },
    update(next: Partial<SiteMapConfig>): void {
      cfg = { ...cfg, ...next };
      paint();
    },
    destroy(): void {
      cap.remove();
    },
    paintedCells: () => wet,
    reach() {
      const m = map.raw();
      const b = m.getBounds();
      const mb = m.getMaxBounds();
      let framable = false;
      if (mb !== null && mb !== undefined) {
        const midLat = (mb.getSouth() + mb.getNorth()) / 2;
        const kmLon =
          (mb.getEast() - mb.getWest()) * 111.32 * Math.cos((midLat * Math.PI) / 180);
        const kmLat = (mb.getNorth() - mb.getSouth()) * 110.57;
        framable = kmLon + 0.001 >= kmLat;
      }
      const inView: Record<string, boolean> = {};
      for (const s of cfg.sources ?? []) {
        if (typeof s.lat === "number" && typeof s.lon === "number") {
          inView[s.name ?? "?"] = b.contains([s.lon, s.lat]);
        }
      }
      return { zoom: m.getZoom(), minZoom: m.getMinZoom(), boundsFramable: framable, inView };
    },
  };
}
